import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';

import {merge} from '#core/utils/config-utils.js';
import {absoluteUrlToInternalResource, getDetectedHostURL} from '#core/utils/url-utils.js';
import {getAdditionalContext, isMobile} from '#core/utils/context-utils.js';

import {Localization} from '#core/localization.js';
import {ApplicationConfig} from '#core/config/application-config.js';
import {DBXPerformanceService} from '#core/services/performance-reporting-service/index.js';

import {AUTH_METHOD} from './api/types.js';
import {DrivenSSOService} from './embedded-app/driven-sso-service.js';

const SystemSettingsTypes = MojitoCore.Services.SystemSettings.types;
const {APPLICATION_MODE} = SystemSettingsTypes;

export function getCommonMojitoConfig({noAuthentication, overlayRandomId}) {
    const initParams = ApplicationConfig.initParams;

    function getAuthServiceConfig(noAuthentication = false) {
        if (!BUILT_IN_AUTHORIZATION_UI) {
            return {
                service: MojitoServices.Authentication.service,
                ssoImplementation: DrivenSSOService,
            };
        }
        if (noAuthentication) {
            // 1. This is a widget
            // 2. It doesn't require authentication service
            return undefined;
        }

        return {
            service: MojitoServices.Authentication.service,
            redirectUrl: `${getDetectedHostURL()}/resetpassword`,
            ssoImplementation:
                ApplicationConfig.initParams.authMethod === AUTH_METHOD.EXTERNAL ? DrivenSSOService : undefined,
        };
    }

    function getServicesConfig() {
        const result = {
            // Common part
            transactionUrl: ApplicationConfig.apiUrl,
            bootstrapService: MojitoServices.Bootstrap.service,
            betsService: {
                service: MojitoServices.Bets.service,
                openBets: !IS_BMB
                    ? {
                          timePeriodInYears: 1,
                          pollIntervalSec: 150,
                      }
                    : undefined,
                settledBets: GENBET_BASED
                    ? {
                          betsAmount: 20,
                          timePeriodInHours: 160,
                          pollIntervalSec: 180,
                      }
                    : undefined,
            },
            userInfoService: !IS_BMB
                ? {
                      service: MojitoServices.UserInfo.service,
                  }
                : undefined,
            authenticationService: getAuthServiceConfig(noAuthentication),
            configurations: {
                AppControllerView: {},
                Image: {
                    svgSpritemap: {
                        idPrefix: 'svgsprite-',
                        path: '',
                    },
                },
                Overlay: {
                    rootElementId: overlayRandomId,
                    style: {
                        container: {
                            zIndex: initParams.overlayPlaneZIndex,
                        },
                    },
                },
                Currencies: initParams.currencyCode
                    ? {
                          defaultCurrencyCode: initParams.currencyCode,
                      }
                    : {},
                ConfigFactory: {
                    strict: false,
                },
                SkeletonPart: {
                    svgSrc: '#svgsprite-skeleton-part',
                },
            },
            stores: {
                content: {
                    contentKeepAliveTime: 5 * 60 * 1000, // Keep data cached for 30 minutes
                },
                systemSettings: {
                    applicationMode: isMobile() ? APPLICATION_MODE.MOBILE : APPLICATION_MODE.DESKTOP,
                    language: Localization.getCurrentLanguage(),
                    additionalContext: getAdditionalContext(),
                    contextToChannelMap: {
                        native: GENBET_BASED ? SystemSettingsTypes.CHANNEL.NATIVE : SystemSettingsTypes.CHANNEL.MOBILE, // not possible to configure promotions for the native context on Quantum-based operators since the native context is not supported by Apollo CMS
                    },
                },
            },
        };
        if (IS_LOTTO) {
            return result;
        } else {
            // Sportsbook
            return merge(result, {
                pdfServiceUrl: !IS_BMB ? ApplicationConfig.apiUrl : undefined,
                promotionsService: {
                    service: MojitoServices.Promotions.pushPromotionsService,
                    // For GenBet integration this service should not be set
                    authService: !GENBET_BASED ? MojitoServices.Promotions.pollPromotionsService : undefined,
                },
                matchAccaService: {
                    service: MojitoServices.MatchAcca.matchAccaService,
                },
                videoStreamingService: !IS_BMB
                    ? {
                          service: MojitoServices.VideoStreaming.service,
                      }
                    : undefined,
                favoritesService: {
                    service: MojitoServices.Favorites.service,
                },
                priceChangePolicy: 'ALLOW_ALL',
                searchService: MojitoServices.Search.searchService,
                stores: {
                    promotions: {
                        // Genbet doesnt support polling
                        pollOnLoggedIn: !GENBET_BASED && !IS_BMB,
                    },
                    search: {
                        recentHistory: {
                            maxSize: 10,
                            maxDays: 180,
                        },
                    },
                },
                configurations: {
                    ApplicationHeader: {
                        appBannerView: {
                            // Just because mojito has this component, it doesn't mean we need to use it.
                            // Override mojito default
                            hide: true,
                        },
                    },
                },
            });
        }
    }

    return {
        core: {
            executionMode:
                process.env.NODE_ENV === 'production'
                    ? MojitoCore.EXECUTION_MODES.RELEASE
                    : MojitoCore.EXECUTION_MODES.DEBUG,
            baseUrl: ApplicationConfig.apiUrl,
            translations: Localization.getMojitoConfig(),
            deviceGuesser: {
                isProbablyHandHeldDevice: isMobile,
            },
            defaultCookieConsentGiven: initParams.cookieConsentGiven,
            // contentTransporter will be set in sportsbook-initializer or lotto-initializer
            services: {
                performanceService: {
                    service: DBXPerformanceService,
                    // serviceConfig: {} // Nothing to configure here. DBXPerformanceService could be configured in runtime
                },
            },
        },
        services: getServicesConfig(),
        resources: {
            getImage: function (path) {
                return absoluteUrlToInternalResource(`/images/${path}`);
            },
            getFixture: function (path) {
                return absoluteUrlToInternalResource(`/fixtures/${path}`);
            },
        },
    };
}
